import { RecordCollection } from './record-collection.js';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FormErrorSummary,
  Input,
  Modal,
  PasswordStrengthInput,
  SelectAllToggle,
  StatusPill,
  Textarea,
  useSnackbar,
  type StatusTone,
  IconButton,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Check,
  CheckCircle2,
  Copy,
  KeyRound,
  Mail,
  PencilLine,
  ShieldCheck,
  UserRoundPlus,
  UsersRound,
  Archive,
  UserCog,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  accessApi,
  getApiError,
  type AccessRole,
  type CompanyMember,
  type MembershipStatus,
} from '../lib/api.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  FilterGroup,
  LoadingCard,
  RequestAlert,
} from './data-view.js';
import type { AccessView } from './list-views.js';
import type { ViewSetter } from './view-state.js';
import { PageHeader } from './page-title.js';

type StatusFilter = 'ALL' | 'INVITED' | 'ACTIVE' | 'INACTIVE';
type RoleFilter = 'ALL' | 'PROTECTED' | 'CUSTOM';
type Tab = 'members' | 'roles';

/// Quais unidades a tela oferece e se quem está vendo pode distribuí-las (ADR 0039). Vem da casca
/// da empresa; com uma unidade só, escolher onde a pessoa atua não tem o que escolher.
export interface MemberUnitAccess {
  units: Array<{ id: string; displayName: string; kind: 'ESTABLISHMENT' | 'SUPPORT' }>;
  canAssign: boolean;
}

export interface AccessCapabilities {
  canInvite: boolean;
  canCreateWithPassword: boolean;
  canViewRoles: boolean;
  canAssignRoles: boolean;
  canCreateRoles: boolean;
  canUpdateRoles: boolean;
  canRemoveRoles: boolean;
  canResetAuthenticator?: boolean;
}

const inviteSchema = z.object({
  firstName: z.string().trim().min(2, 'Informe o nome.'),
  lastName: z.string().trim().min(2, 'Informe o sobrenome.'),
  email: z.email('Informe um e-mail válido.'),
  password: z.string().optional(),
});
/// Criar com senha usa a mesma régua de senha do primeiro acesso e da ativação por convite.
const memberWithPasswordSchema = inviteSchema.extend({
  password: z
    .string()
    .min(8, 'Use ao menos 8 caracteres.')
    .regex(/[A-Z]/, 'Inclua uma letra maiúscula.')
    .regex(/\d/, 'Inclua um número.')
    .regex(/[^A-Za-z0-9]/, 'Inclua um símbolo.'),
});
type InviteForm = z.infer<typeof inviteSchema> & { password?: string | undefined };
/// Convidar e criar com senha levam ao mesmo lugar; muda quem executa o primeiro passo.
type AddMethod = 'invite' | 'password';

const roleSchema = z.object({
  displayName: z
    .string()
    .trim()
    .min(2, 'Informe o nome do perfil.')
    .max(60, 'Use até 60 caracteres.'),
  description: z.string().trim().max(500, 'Use até 500 caracteres.'),
});
type RoleForm = z.infer<typeof roleSchema>;

const statusFilters: Array<{ value: StatusFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INVITED', label: 'Convites pendentes' },
  { value: 'INACTIVE', label: 'Inativos' },
];

const roleFilters: Array<{ value: RoleFilter; label: string }> = [
  { value: 'ALL', label: 'Todos' },
  { value: 'PROTECTED', label: 'Protegidos' },
  { value: 'CUSTOM', label: 'Personalizados' },
];

export function CompanyAccess({
  capabilities,
  currentUserId,
  unitAccess = { units: [], canAssign: false },
  view,
  onView,
}: {
  capabilities: AccessCapabilities;
  currentUserId?: string;
  unitAccess?: MemberUnitAccess;
  view: AccessView;
  onView: ViewSetter<AccessView>;
}) {
  const { tab } = view;
  const canSeeRoles = capabilities.canViewRoles;

  /// Trocar de aba limpa o recorte: `search` é o mesmo parâmetro nos dois painéis, e `status` e
  /// `kind` são de painéis diferentes. Levar o filtro de Pessoas para Perfis mostraria uma lista
  /// recortada por algo que a aba nova nem oferece.
  const chooseTab = (next: Tab) => {
    onView({ tab: next, search: '', status: 'ALL', kind: 'ALL' });
  };

  return (
    <section aria-labelledby="company-access-title">
      <PageHeader
        trail="Configurações / Pessoas e acessos"
        id="company-access-title"
        title="Pessoas e acessos"
        description="Defina quem entra na empresa e o que cada pessoa pode fazer."
      />

      {canSeeRoles ? (
        <div
          role="tablist"
          aria-label="Seções de pessoas e acessos"
          className="mb-6 flex flex-wrap border-b border-surface-border"
        >
          {(
            [
              { value: 'members', label: 'Pessoas' },
              { value: 'roles', label: 'Perfis de acesso' },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              id={`access-tab-${option.value}`}
              aria-selected={tab === option.value}
              aria-controls={`access-panel-${option.value}`}
              onClick={() => chooseTab(option.value)}
              className={`min-h-10 border-b-2 px-4 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                tab === option.value
                  ? 'border-brand-400 text-brand-400'
                  : 'border-transparent text-content-muted hover:text-content'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}

      {tab === 'roles' && canSeeRoles ? (
        <div role="tabpanel" id="access-panel-roles" aria-labelledby="access-tab-roles">
          <AccessRolesPanel capabilities={capabilities} view={view} onView={onView} />
        </div>
      ) : (
        <div role="tabpanel" id="access-panel-members" aria-labelledby="access-tab-members">
          <MembersPanel
            capabilities={capabilities}
            currentUserId={currentUserId}
            unitAccess={unitAccess}
            view={view}
            onView={onView}
          />
        </div>
      )}
    </section>
  );
}

function MembersPanel({
  capabilities,
  currentUserId,
  unitAccess,
  view,
  onView,
}: {
  capabilities: AccessCapabilities;
  currentUserId: string | undefined;
  unitAccess: MemberUnitAccess;
  view: AccessView;
  onView: ViewSetter<AccessView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const { search, status } = view;
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [roleError, setRoleError] = useState<string>();
  const [requestError, setRequestError] = useState<string>();
  const [activationLink, setActivationLink] = useState<string>();
  const [authenticatorMember, setAuthenticatorMember] = useState<CompanyMember>();
  const canResetFor = (member: CompanyMember) =>
    capabilities.canResetAuthenticator === true &&
    member.status === 'ACTIVE' &&
    member.userId !== currentUserId;
  const [method, setMethod] = useState<AddMethod>('invite');
  const [createdMember, setCreatedMember] = useState<CompanyMember>();
  const [copied, setCopied] = useState(false);
  const [editingMember, setEditingMember] = useState<CompanyMember>();
  const [unitsMember, setUnitsMember] = useState<CompanyMember>();
  const showsUnits = unitAccess.units.length > 1;
  const [savingRoles, setSavingRoles] = useState(false);
  const creatingWithPassword = method === 'password';
  const form = useForm<InviteForm>({
    resolver: (values, context, options) =>
      zodResolver(creatingWithPassword ? memberWithPasswordSchema : inviteSchema)(
        values,
        context,
        options,
      ),
    defaultValues: { firstName: '', lastName: '', email: '', password: '' },
  });
  const passwordValue = useWatch({ control: form.control, name: 'password' });
  const members = useQuery({
    queryKey: ['company', 'members', search, status],
    queryFn: () =>
      accessApi.members({ ...(search.trim() ? { search: search.trim() } : {}), status }),
    retry: false,
  });
  const memberCount = members.data?.length ?? 0;
  /// O filtro ativo vira etiqueta removível na barra, como nas outras listagens.
  const activeFilters =
    status === 'ALL'
      ? []
      : [
          {
            key: 'status',
            label: statusFilters.find((item) => item.value === status)!.label,
            onRemove: () => onView({ status: 'ALL' }),
          },
        ];
  const canAddPeople = capabilities.canInvite || capabilities.canCreateWithPassword;
  const canChooseRoles = canAddPeople || capabilities.canAssignRoles;
  const roles = useQuery({
    queryKey: ['company', 'access-roles'],
    queryFn: accessApi.roles,
    retry: false,
    enabled: canChooseRoles,
  });

  function openInvite() {
    form.reset();
    setSelectedRoleIds([]);
    setRoleError(undefined);
    setRequestError(undefined);
    setActivationLink(undefined);
    setCreatedMember(undefined);
    setMethod(capabilities.canInvite ? 'invite' : 'password');
    setCopied(false);
    setInviteOpen(true);
  }

  function changeMethod(next: AddMethod) {
    setMethod(next);
    setRequestError(undefined);
    form.clearErrors('password');
  }

  function openMemberRoles(member: CompanyMember) {
    setSelectedRoleIds(member.roles.map((role) => role.id));
    setRoleError(undefined);
    setRequestError(undefined);
    setEditingMember(member);
  }

  function toggleRole(roleId: string) {
    setRoleError(undefined);
    setSelectedRoleIds((current) =>
      current.includes(roleId) ? current.filter((id) => id !== roleId) : [...current, roleId],
    );
  }

  function chooseRoles(roleIds: readonly string[], on: boolean) {
    setRoleError(undefined);
    setSelectedRoleIds((current) =>
      on
        ? [...current, ...roleIds.filter((id) => !current.includes(id))]
        : current.filter((id) => !roleIds.includes(id)),
    );
  }

  async function invite(data: InviteForm) {
    if (selectedRoleIds.length === 0) {
      setRoleError('Selecione ao menos um perfil de acesso.');
      return;
    }
    setRequestError(undefined);
    const { password, ...person } = data;
    try {
      if (creatingWithPassword) {
        const result = await accessApi.create({
          ...person,
          password: password ?? '',
          roleIds: selectedRoleIds,
        });
        setCreatedMember(result.member);
      } else {
        const result = await accessApi.invite({ ...person, roleIds: selectedRoleIds });
        const url = new URL('/activate-member', window.location.origin);
        url.searchParams.set('token', result.activationToken);
        setActivationLink(url.toString());
      }
      await queryClient.invalidateQueries({ queryKey: ['company', 'members'] });
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  async function saveMemberRoles() {
    if (!editingMember) return;
    if (selectedRoleIds.length === 0) {
      setRoleError('Selecione ao menos um perfil de acesso.');
      return;
    }
    setRequestError(undefined);
    setSavingRoles(true);
    try {
      await accessApi.updateMemberRoles(editingMember.id, selectedRoleIds);
      await queryClient.invalidateQueries({ queryKey: ['company', 'members'] });
      await queryClient.invalidateQueries({ queryKey: ['company', 'access-roles'] });
      setEditingMember(undefined);
      // O diálogo fecha e a linha da pessoa volta a ser uma entre várias: sem o aviso, salvar e
      // desistir têm exatamente a mesma aparência.
      snackbar.success(`Perfis de ${editingMember.firstName} atualizados.`);
    } catch (error) {
      setRequestError(getApiError(error));
    } finally {
      setSavingRoles(false);
    }
  }

  async function copyLink() {
    if (!activationLink) return;
    try {
      await navigator.clipboard.writeText(activationLink);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div>
      {canAddPeople ? (
        <div className="mb-5 flex justify-end">
          <Button onClick={openInvite}>
            <UserRoundPlus className="size-4" />
            {capabilities.canCreateWithPassword ? 'Adicionar pessoa' : 'Convidar pessoa'}
          </Button>
        </div>
      ) : null}

      <FilterBar
        searchId="member-search"
        searchLabel="Buscar pessoa"
        searchPlaceholder="Nome ou e-mail"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={() => onView({ status: 'ALL' })}
        count={`${String(memberCount)} ${memberCount === 1 ? 'pessoa' : 'pessoas'}`}
        resultsLabel={`Ver ${String(memberCount)} ${memberCount === 1 ? 'pessoa' : 'pessoas'}`}
      >
        <FilterGroup
          label="Situação"
          description="Filtrar pessoas por situação"
          options={statusFilters}
          value={status}
          onChange={(value) => onView({ status: value })}
        />
      </FilterBar>

      {members.isLoading ? (
        <LoadingCard label="Carregando pessoas" />
      ) : members.error ? (
        <ErrorCard message={getApiError(members.error)} />
      ) : members.data?.length ? (
        <RecordCollection
          label="Pessoas com acesso"
          records={members.data}
          recordKey={(member) => member.id}
          columns={[
            {
              key: 'name',
              label: 'Pessoa',
              value: (member) => `${member.firstName} ${member.lastName}`,
              render: (member) => (
                <strong>
                  {member.firstName} {member.lastName}
                </strong>
              ),
            },
            {
              key: 'email',
              label: 'E-mail',
              value: (member) => member.email,
              render: (member) => (
                <span className="text-xs text-content-muted">{member.email}</span>
              ),
            },
            {
              key: 'roles',
              label: 'Perfis',
              render: (member) => (
                <span className="text-xs">
                  {member.roles.map((role) => role.displayName).join(' · ')}
                </span>
              ),
            },
            ...(showsUnits
              ? [
                  {
                    key: 'units',
                    label: 'Unidades',
                    render: (member: CompanyMember) => (
                      <span className="text-xs">
                        {member.unitScope === 'ALL'
                          ? 'Todas'
                          : member.businessUnits.map((unit) => unit.displayName).join(', ')}
                      </span>
                    ),
                  },
                ]
              : []),
            {
              key: 'status',
              label: 'Situação',
              value: (member) => member.status,
              render: (member) => <MemberStatusPill status={member.status} />,
            },
          ]}
          actions={(member) => (
            <>
              {capabilities.canAssignRoles ? (
                <IconButton
                  label="Gerenciar perfis"
                  icon={<UserCog />}
                  onClick={() => openMemberRoles(member)}
                />
              ) : null}
              {showsUnits && unitAccess.canAssign ? (
                <IconButton
                  label="Unidades"
                  icon={<Building2 />}
                  onClick={() => setUnitsMember(member)}
                />
              ) : null}
              {canResetFor(member) ? (
                <IconButton
                  label="Redefinir autenticador"
                  icon={<KeyRound />}
                  onClick={() => setAuthenticatorMember(member)}
                />
              ) : null}
            </>
          )}
          card={(member) => (
            <ul>
              <MemberRow
                member={member}
                canAssignRoles={capabilities.canAssignRoles}
                onManage={() => openMemberRoles(member)}
                showsUnits={showsUnits}
                canAssignUnits={showsUnits && unitAccess.canAssign}
                onManageUnits={() => setUnitsMember(member)}
                canResetAuthenticator={canResetFor(member)}
                onResetAuthenticator={() => setAuthenticatorMember(member)}
              />
            </ul>
          )}
        />
      ) : (
        <EmptyCard
          icon={UsersRound}
          title="Nenhuma pessoa encontrada"
          description="Ajuste a busca ou os filtros aplicados."
        />
      )}

      {authenticatorMember ? (
        <ResetAuthenticatorModal
          member={authenticatorMember}
          onClose={() => setAuthenticatorMember(undefined)}
        />
      ) : null}

      <Modal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        title={
          activationLink
            ? 'Convite enviado'
            : createdMember
              ? 'Acesso criado'
              : capabilities.canCreateWithPassword
                ? 'Adicionar pessoa'
                : 'Convidar pessoa'
        }
        description={
          activationLink
            ? 'O convite foi enviado por e-mail para a pessoa indicada.'
            : createdMember
              ? 'Entregue a senha à pessoa agora; ela vale só para o primeiro acesso.'
              : 'Escolha os perfis que determinam o acesso inicial.'
        }
      >
        {createdMember ? (
          <div>
            <div className="mb-5 flex gap-3 rounded-xl border border-positive/30 bg-positive/10 p-4">
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-positive" />
              <p className="text-sm leading-6 text-content-secondary">
                {createdMember.firstName} já pode entrar com o e-mail e a senha que você definiu. No
                primeiro acesso, o sistema pede o aplicativo autenticador e uma senha nova, que só
                ela conhecerá.
              </p>
            </div>
            <Input label="E-mail de acesso" value={createdMember.email} readOnly />
            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
              <Button type="button" onClick={() => setInviteOpen(false)}>
                Fechar
              </Button>
            </div>
          </div>
        ) : activationLink ? (
          <div>
            <div className="mb-5 flex gap-3 rounded-xl border border-positive/30 bg-positive/10 p-4">
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-positive" />
              <p className="text-sm leading-6 text-content-secondary">
                O convite expira em 24 horas e pode ser utilizado uma única vez.
              </p>
            </div>
            <Input
              label="Link de ativação"
              value={activationLink}
              readOnly
              hint="Use este link apenas se o e-mail não chegar — entregue-o somente à pessoa convidada."
            />
            <div className="mt-6 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Fechar
              </Button>
              <Button type="button" onClick={() => void copyLink()}>
                {copied ? <CheckCircle2 className="size-4" /> : <Copy className="size-4" />}
                {copied ? 'Link copiado' : 'Copiar link'}
              </Button>
            </div>
          </div>
        ) : (
          <form noValidate onSubmit={(event) => void form.handleSubmit(invite)(event)}>
            {capabilities.canInvite && capabilities.canCreateWithPassword ? (
              <div
                role="radiogroup"
                aria-label="Como esta pessoa recebe o acesso"
                className="mb-6 grid gap-3 sm:grid-cols-2"
              >
                {[
                  {
                    value: 'invite' as const,
                    title: 'Enviar convite',
                    hint: 'A pessoa escolhe a própria senha pelo link.',
                  },
                  {
                    value: 'password' as const,
                    title: 'Definir senha agora',
                    hint: 'Você entrega a senha e ela troca no primeiro acesso.',
                  },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={method === option.value}
                    onClick={() => changeMethod(option.value)}
                    className={`cursor-pointer rounded-xl border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                      method === option.value
                        ? 'border-brand-500/60 bg-brand-500/10'
                        : 'border-surface-border bg-surface-elevated hover:border-brand-500/40'
                    }`}
                  >
                    <strong className="block text-sm">{option.title}</strong>
                    <span className="mt-0.5 block text-xs leading-5 text-content-muted">
                      {option.hint}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            <div className="grid gap-5 sm:grid-cols-2">
              <Input
                label="Nome"
                autoComplete="given-name"
                error={form.formState.errors.firstName?.message}
                {...form.register('firstName')}
              />
              <Input
                label="Sobrenome"
                autoComplete="family-name"
                error={form.formState.errors.lastName?.message}
                {...form.register('lastName')}
              />
              <Input
                className="sm:col-span-2"
                label="E-mail"
                type="email"
                autoComplete="email"
                leftIcon={<Mail className="size-4" />}
                error={form.formState.errors.email?.message}
                {...form.register('email')}
              />
              {creatingWithPassword ? (
                <div className="sm:col-span-2">
                  <PasswordStrengthInput
                    label="Senha inicial"
                    autoComplete="new-password"
                    name="password"
                    onBlur={() => void form.trigger('password')}
                    value={passwordValue ?? ''}
                    onValueChange={(value) =>
                      form.setValue('password', value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      })
                    }
                    error={form.formState.errors.password?.message}
                  />
                  <p className="mt-2 text-xs leading-5 text-content-muted">
                    Entregue esta senha à pessoa; ela vale só para o primeiro acesso.
                  </p>
                </div>
              ) : null}
            </div>
            <RoleChooser
              roles={roles.data ?? []}
              selectedIds={selectedRoleIds}
              onToggle={toggleRole}
              onChooseAll={chooseRoles}
              error={roleError}
            />
            {requestError ? <RequestAlert message={requestError} /> : null}
            <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
              <FormErrorSummary className="order-last sm:order-first sm:mr-auto sm:flex-1" />
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Cancelar
              </Button>
              <Button loading={form.formState.isSubmitting}>
                {creatingWithPassword ? 'Criar acesso' : 'Enviar convite'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={Boolean(editingMember)}
        onOpenChange={(open) => {
          if (!open) setEditingMember(undefined);
        }}
        title="Perfis de acesso da pessoa"
        {...(editingMember
          ? { description: `Defina o que ${editingMember.firstName} pode fazer na empresa.` }
          : {})}
      >
        <RoleChooser
          roles={roles.data ?? []}
          selectedIds={selectedRoleIds}
          onToggle={toggleRole}
          onChooseAll={chooseRoles}
          error={roleError}
        />
        {requestError ? <RequestAlert message={requestError} /> : null}
        <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => setEditingMember(undefined)}>
            Cancelar
          </Button>
          <Button type="button" loading={savingRoles} onClick={() => void saveMemberRoles()}>
            Salvar perfis
          </Button>
        </div>
      </Modal>

      {unitsMember ? (
        <MemberUnitsDialog
          member={unitsMember}
          units={unitAccess.units}
          onClose={() => setUnitsMember(undefined)}
        />
      ) : null}
    </div>
  );
}

/// Onde a pessoa atua. O perfil diz o que ela faz; isto diz em quais unidades (ADR 0039).
function MemberUnitsDialog({
  member,
  units,
  onClose,
}: {
  member: CompanyMember;
  units: MemberUnitAccess['units'];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [scope, setScope] = useState(member.unitScope);
  const [selectedIds, setSelectedIds] = useState(member.businessUnits.map((unit) => unit.id));
  const [error, setError] = useState<string>();
  const [requestError, setRequestError] = useState<string>();
  const [saving, setSaving] = useState(false);
  // O servidor ignora o recorte para quem administra a empresa; dizer antes evita a pessoa achar
  // que restringiu e descobrir depois que não.
  const seesEverythingAnyway = member.roles.some((role) =>
    ['TENANT_OWNER', 'TENANT_ADMIN'].includes(role.key),
  );

  function toggle(unitId: string) {
    setError(undefined);
    setSelectedIds((current) =>
      current.includes(unitId) ? current.filter((id) => id !== unitId) : [...current, unitId],
    );
  }

  async function save() {
    if (scope === 'SELECTED' && selectedIds.length === 0) {
      setError('Escolha ao menos uma unidade.');
      return;
    }
    setRequestError(undefined);
    setSaving(true);
    try {
      await accessApi.updateMemberUnits(member.id, {
        unitScope: scope,
        businessUnitIds: scope === 'ALL' ? [] : selectedIds,
      });
      await queryClient.invalidateQueries({ queryKey: ['company', 'members'] });
      onClose();
      snackbar.success(`Unidades de ${member.firstName} atualizadas.`);
    } catch (failure) {
      setRequestError(getApiError(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Unidades da pessoa"
      description={`Defina em quais unidades ${member.firstName} atua.`}
    >
      <fieldset>
        <legend className="text-xs font-semibold text-content/70">Alcance</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <SelectableOption
            selected={scope === 'ALL'}
            onClick={() => {
              setError(undefined);
              setScope('ALL');
            }}
            title="Todas as unidades"
            description="Inclui as unidades criadas depois."
          />
          <SelectableOption
            selected={scope === 'SELECTED'}
            onClick={() => setScope('SELECTED')}
            title="Somente as escolhidas"
            description="Unidade nova fica de fora até ser marcada."
          />
        </div>
      </fieldset>

      {scope === 'SELECTED' ? (
        <fieldset className="mt-6">
          <legend className="text-xs font-semibold text-content/70">Unidades</legend>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {units.map((unit) => (
              <SelectableOption
                key={unit.id}
                selected={selectedIds.includes(unit.id)}
                onClick={() => toggle(unit.id)}
                title={unit.displayName}
                description={unit.kind === 'SUPPORT' ? 'Apoio' : 'Estabelecimento'}
              />
            ))}
          </div>
          {error ? (
            <p role="alert" className="mt-2 text-xs text-danger">
              {error}
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {seesEverythingAnyway ? (
        <p
          role="status"
          className="mt-5 rounded-xl border border-surface-border bg-surface-elevated px-4 py-3 text-xs text-content-muted"
        >
          {member.firstName} é proprietário ou administrador: continua alcançando todas as unidades
          enquanto tiver esse perfil.
        </p>
      ) : null}

      {requestError ? <RequestAlert message={requestError} /> : null}
      <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button type="button" loading={saving} onClick={() => void save()}>
          Salvar unidades
        </Button>
      </div>
    </Modal>
  );
}

function AccessRolesPanel({
  capabilities,
  view,
  onView,
}: {
  capabilities: AccessCapabilities;
  view: AccessView;
  onView: ViewSetter<AccessView>;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const { search, kind } = view;
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<AccessRole>();
  const [removingRole, setRemovingRole] = useState<AccessRole>();
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [permissionError, setPermissionError] = useState<string>();
  const [requestError, setRequestError] = useState<string>();
  const [removing, setRemoving] = useState(false);
  const form = useForm<RoleForm>({
    resolver: zodResolver(roleSchema),
    defaultValues: { displayName: '', description: '' },
  });
  const roles = useQuery({
    queryKey: ['company', 'access-roles'],
    queryFn: accessApi.roles,
    retry: false,
  });
  const permissions = useQuery({
    queryKey: ['company', 'permissions'],
    queryFn: accessApi.permissions,
    retry: false,
    enabled: capabilities.canCreateRoles || capabilities.canUpdateRoles,
  });

  const term = search.trim().toLowerCase();
  const visibleRoles = (roles.data ?? []).filter(
    (role) =>
      (kind === 'ALL' ||
        (kind === 'PROTECTED' && role.protected) ||
        (kind === 'CUSTOM' && !role.protected)) &&
      (term.length === 0 ||
        role.displayName.toLowerCase().includes(term) ||
        (role.description ?? '').toLowerCase().includes(term)),
  );
  const activeFilters =
    kind === 'ALL'
      ? []
      : [
          {
            key: 'kind',
            label: roleFilters.find((item) => item.value === kind)!.label,
            onRemove: () => onView({ kind: 'ALL' }),
          },
        ];

  function openEditor(role?: AccessRole) {
    form.reset({ displayName: role?.displayName ?? '', description: role?.description ?? '' });
    setSelectedPermissions(role ? [...role.permissionKeys] : []);
    setEditingRole(role);
    setPermissionError(undefined);
    setRequestError(undefined);
    setEditorOpen(true);
  }

  function togglePermission(key: string) {
    setPermissionError(undefined);
    setSelectedPermissions((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  }

  const allPermissionKeys = (permissions.data ?? []).flatMap((entry) =>
    entry.permissions.map((permission) => permission.key),
  );

  function setPermissionGroup(keys: readonly string[], on: boolean) {
    setPermissionError(undefined);
    setSelectedPermissions((current) =>
      on
        ? [...current, ...keys.filter((key) => !current.includes(key))]
        : current.filter((key) => !keys.includes(key)),
    );
  }

  async function save(data: RoleForm) {
    if (selectedPermissions.length === 0) {
      setPermissionError('Selecione ao menos uma permissão.');
      return;
    }
    setRequestError(undefined);
    const payload = {
      displayName: data.displayName,
      description: data.description.trim() ? data.description.trim() : null,
      permissionKeys: selectedPermissions,
    };
    try {
      if (editingRole) await accessApi.updateRole(editingRole.id, payload);
      else await accessApi.createRole(payload);
      await queryClient.invalidateQueries({ queryKey: ['company', 'access-roles'] });
      await queryClient.invalidateQueries({ queryKey: ['company', 'members'] });
      setEditorOpen(false);
      snackbar.success(
        editingRole
          ? `Perfil ${data.displayName} atualizado.`
          : `Perfil ${data.displayName} criado.`,
      );
    } catch (error) {
      setRequestError(getApiError(error));
    }
  }

  async function remove() {
    if (!removingRole) return;
    setRequestError(undefined);
    setRemoving(true);
    try {
      await accessApi.deactivateRole(removingRole.id);
      await queryClient.invalidateQueries({ queryKey: ['company', 'access-roles'] });
      setRemovingRole(undefined);
      // Desativar tira a linha da lista filtrada por padrão. Some o diálogo, some o perfil: sem o
      // aviso, o mesmo desaparecimento serve para "deu certo" e para "estava vendo coisa errada".
      snackbar.success(`Perfil ${removingRole.displayName} desativado.`);
    } catch (error) {
      setRequestError(getApiError(error));
    } finally {
      setRemoving(false);
    }
  }

  function roleActions(role: AccessRole) {
    return role.protected ? (
      <span className="self-start rounded-full border border-surface-border bg-surface-elevated px-3 py-1 text-xs text-content-secondary sm:self-auto">
        Protegido
      </span>
    ) : (
      <div className="flex flex-wrap gap-1">
        {capabilities.canUpdateRoles ? (
          <IconButton label="Editar" icon={<PencilLine />} onClick={() => openEditor(role)} />
        ) : null}
        {capabilities.canRemoveRoles ? (
          <IconButton
            label="Desativar"
            icon={<Archive />}
            tone="danger"
            onClick={() => {
              setRequestError(undefined);
              setRemovingRole(role);
            }}
          />
        ) : null}
      </div>
    );
  }
  return (
    <div>
      {capabilities.canCreateRoles ? (
        <div className="mb-5 flex justify-end">
          <Button onClick={() => openEditor()}>
            <KeyRound className="size-4" />
            Criar perfil
          </Button>
        </div>
      ) : null}

      <FilterBar
        searchId="role-search"
        searchLabel="Buscar perfil"
        searchPlaceholder="Nome ou descrição"
        search={search}
        onSearchChange={(value) => onView({ search: value }, { replace: true })}
        chips={activeFilters}
        onClear={() => onView({ kind: 'ALL' })}
        count={`${String(visibleRoles.length)} ${visibleRoles.length === 1 ? 'perfil' : 'perfis'}`}
        resultsLabel={`Ver ${String(visibleRoles.length)} ${visibleRoles.length === 1 ? 'perfil' : 'perfis'}`}
      >
        <FilterGroup
          label="Tipo"
          description="Filtrar perfis por tipo"
          options={roleFilters}
          value={kind}
          onChange={(value) => onView({ kind: value })}
        />
      </FilterBar>

      {roles.isLoading ? (
        <LoadingCard label="Carregando perfis" />
      ) : roles.error ? (
        <ErrorCard message={getApiError(roles.error)} />
      ) : visibleRoles.length ? (
        <RecordCollection
          label="Perfis de acesso"
          records={visibleRoles}
          recordKey={(role) => role.id}
          columns={[
            {
              key: 'name',
              label: 'Perfil',
              value: (role) => role.displayName,
              render: (role) => <strong>{role.displayName}</strong>,
            },
            {
              key: 'description',
              label: 'Descrição',
              render: (role) => (
                <span className="text-xs text-content-muted">
                  {role.description ?? 'Acesso personalizado desta empresa.'}
                </span>
              ),
            },
            {
              key: 'permissions',
              label: 'Permissões',
              value: (role) => role.permissionKeys.length,
              render: (role) => role.permissionKeys.length,
            },
            {
              key: 'members',
              label: 'Pessoas',
              value: (role) => role.memberCount,
              render: (role) => role.memberCount,
            },
          ]}
          actions={roleActions}
          card={(role) => (
            <article
              key={role.id}
              className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center"
            >
              <span className="grid size-11 shrink-0 place-items-center rounded-full bg-brand-500/12 text-brand-400">
                {role.protected ? (
                  <ShieldCheck className="size-5" aria-label="Perfil protegido" />
                ) : (
                  <KeyRound className="size-5" aria-hidden="true" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <h2 className="truncate font-semibold">{role.displayName}</h2>
                <p className="hue-list-description mt-1 text-sm leading-6 text-content-muted">
                  {role.description ?? 'Acesso personalizado desta empresa.'}
                </p>
                <p className="mt-2 text-xs text-content-faint">
                  {role.permissionKeys.length}{' '}
                  {role.permissionKeys.length === 1 ? 'permissão' : 'permissões'} ·{' '}
                  {role.memberCount} {role.memberCount === 1 ? 'pessoa' : 'pessoas'}
                </p>
              </div>
              {roleActions(role)}
            </article>
          )}
        />
      ) : (
        <EmptyCard
          icon={KeyRound}
          title="Nenhum perfil encontrado"
          description="Ajuste a busca ou crie um perfil personalizado."
        />
      )}

      <Modal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        title={editingRole ? 'Editar perfil de acesso' : 'Criar perfil de acesso'}
        description="Escolha o que este perfil permite fazer na empresa."
      >
        <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
          <div className="grid gap-5">
            <Input
              label="Nome do perfil"
              error={form.formState.errors.displayName?.message}
              {...form.register('displayName')}
            />
            <Textarea
              label="Descrição"
              hint="Explique em uma frase o que este perfil faz."
              error={form.formState.errors.description?.message}
              {...form.register('description')}
            />
          </div>
          <fieldset className="mt-6">
            <legend className="float-left text-xs font-semibold text-content/70">Permissões</legend>
            <SelectAllToggle
              className="float-right"
              scope="todas as permissões"
              total={allPermissionKeys.length}
              selected={allPermissionKeys.filter((key) => selectedPermissions.includes(key)).length}
              onSelectAll={() => setPermissionGroup(allPermissionKeys, true)}
              onClear={() => setPermissionGroup(allPermissionKeys, false)}
            />
            <div className="clear-both space-y-5 pt-3">
              {(permissions.data ?? []).map((entry) => (
                <div key={entry.module}>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <p className="text-[10px] font-bold uppercase tracking-[.12em] text-content-faint">
                      {entry.module}
                    </p>
                    <SelectAllToggle
                      scope={entry.module}
                      total={entry.permissions.length}
                      selected={
                        entry.permissions.filter((permission) =>
                          selectedPermissions.includes(permission.key),
                        ).length
                      }
                      onSelectAll={() =>
                        setPermissionGroup(
                          entry.permissions.map((permission) => permission.key),
                          true,
                        )
                      }
                      onClear={() =>
                        setPermissionGroup(
                          entry.permissions.map((permission) => permission.key),
                          false,
                        )
                      }
                    />
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {entry.permissions.map((permission) => (
                      <SelectableOption
                        key={permission.key}
                        selected={selectedPermissions.includes(permission.key)}
                        onClick={() => togglePermission(permission.key)}
                        title={permission.displayName}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {permissionError ? (
              <p className="mt-2 text-xs text-danger" role="alert">
                {permissionError}
              </p>
            ) : null}
          </fieldset>
          {requestError ? <RequestAlert message={requestError} /> : null}
          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
            <FormErrorSummary className="order-last sm:order-first sm:mr-auto sm:flex-1" />
            <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>
              Cancelar
            </Button>
            <Button loading={form.formState.isSubmitting}>
              {editingRole ? 'Salvar perfil' : 'Criar perfil'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={Boolean(removingRole)}
        onOpenChange={(open) => {
          if (!open) setRemovingRole(undefined);
        }}
        title="Desativar perfil de acesso"
        {...(removingRole
          ? {
              description: `O perfil ${removingRole.displayName} deixa de ficar disponível para novas atribuições.`,
            }
          : {})}
      >
        {requestError ? <RequestAlert message={requestError} /> : null}
        <div className="mt-2 flex flex-col-reverse gap-3 border-t border-surface-border pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => setRemovingRole(undefined)}>
            Manter perfil
          </Button>
          <Button type="button" loading={removing} onClick={() => void remove()}>
            Desativar perfil
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function RoleChooser({
  roles,
  selectedIds,
  onToggle,
  onChooseAll,
  error,
}: {
  roles: AccessRole[];
  selectedIds: string[];
  onToggle: (roleId: string) => void;
  onChooseAll: (roleIds: readonly string[], on: boolean) => void;
  error?: string | undefined;
}) {
  return (
    <fieldset className="mt-6">
      <legend className="float-left text-xs font-semibold text-content/70">Perfis de acesso</legend>
      <SelectAllToggle
        className="float-right"
        scope="perfis de acesso"
        total={roles.length}
        selected={roles.filter((role) => selectedIds.includes(role.id)).length}
        onSelectAll={() =>
          onChooseAll(
            roles.map((role) => role.id),
            true,
          )
        }
        onClear={() =>
          onChooseAll(
            roles.map((role) => role.id),
            false,
          )
        }
      />
      <div className="clear-both grid gap-2 pt-2">
        {roles.map((role) => (
          <SelectableOption
            key={role.id}
            selected={selectedIds.includes(role.id)}
            onClick={() => onToggle(role.id)}
            title={role.displayName}
            description={role.description ?? 'Acesso personalizado para esta empresa.'}
            badge={
              role.protected ? (
                <ShieldCheck
                  className="size-4 shrink-0 text-brand-400"
                  aria-label="Perfil protegido"
                />
              ) : null
            }
          />
        ))}
      </div>
      {error ? (
        <p className="mt-2 text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

function SelectableOption({
  selected,
  onClick,
  title,
  description,
  badge,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  description?: string | undefined;
  badge?: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={`flex min-h-14 items-center gap-3 rounded-xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
        selected
          ? 'border-brand-500/60 bg-brand-500/10'
          : 'border-surface-border bg-surface-elevated hover:border-brand-500/35'
      }`}
    >
      <span
        className={`grid size-8 shrink-0 place-items-center rounded-lg border ${
          selected
            ? 'border-brand-500 bg-brand-500 text-on-brand'
            : 'border-surface-border text-transparent'
        }`}
      >
        <Check className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <strong className="block text-sm">{title}</strong>
        {description ? (
          <span className="mt-0.5 block text-xs leading-5 text-content-muted">{description}</span>
        ) : null}
      </span>
      {badge}
    </button>
  );
}

function MemberRow({
  member,
  canAssignRoles,
  onManage,
  showsUnits = false,
  canAssignUnits = false,
  onManageUnits,
  canResetAuthenticator = false,
  onResetAuthenticator,
}: {
  member: CompanyMember;
  canAssignRoles: boolean;
  onManage: () => void;
  showsUnits?: boolean;
  canAssignUnits?: boolean;
  onManageUnits?: () => void;
  canResetAuthenticator?: boolean;
  onResetAuthenticator?: () => void;
}) {
  return (
    <li className="flex flex-col gap-4 px-5 py-5 sm:flex-row sm:items-center">
      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-brand-500/12 font-semibold text-brand-400">
        {member.firstName.slice(0, 1).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="truncate font-semibold">
            {member.firstName} {member.lastName}
          </h2>
          <MemberStatusPill status={member.status} />
        </div>
        <p className="mt-1 truncate text-sm text-content-muted">{member.email}</p>
        {showsUnits ? (
          <p className="mt-1 truncate text-xs text-content-faint">
            Unidades:{' '}
            {member.unitScope === 'ALL'
              ? 'todas'
              : member.businessUnits.map((unit) => unit.displayName).join(', ')}
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2 sm:max-w-[45%] sm:justify-end">
        {member.roles.map((role) => (
          <span
            key={role.id}
            className="rounded-full border border-surface-border bg-surface-elevated px-3 py-1 text-xs text-content-secondary"
          >
            {role.displayName}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-1">
        {canAssignRoles ? (
          <IconButton label="Gerenciar perfis" icon={<UserCog />} onClick={onManage} />
        ) : null}
        {canAssignUnits ? (
          <IconButton label="Unidades" icon={<Building2 />} onClick={onManageUnits} />
        ) : null}
        {canResetAuthenticator ? (
          <IconButton
            label="Redefinir autenticador"
            icon={<KeyRound />}
            onClick={onResetAuthenticator}
          />
        ) : null}
      </div>
    </li>
  );
}

/// Redefinir exige o código de quem redefine: sessão aberta sozinha não abre a porta de outra pessoa
/// (ADR 0081). O que cabe a ela depois é só cadastrar de novo na próxima entrada.
function ResetAuthenticatorModal({
  member,
  onClose,
}: {
  member: CompanyMember;
  onClose: () => void;
}) {
  const snackbar = useSnackbar();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!/^\d{6}$/u.test(code)) {
      setError('Informe os 6 dígitos do seu código.');
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await accessApi.resetMemberAuthenticator(member.id, code);
      snackbar.success(
        `Autenticador de ${member.firstName} redefinido. Avise para cadastrar de novo ao entrar.`,
      );
      onClose();
    } catch (requestError) {
      setError(getApiError(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Redefinir autenticador"
      description={`Para quando ${member.firstName} ${member.lastName} perdeu o acesso ao aplicativo autenticador.`}
    >
      <form noValidate className="space-y-4" onSubmit={(event) => void submit(event)}>
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-6 text-content-secondary">
          <li>O código atual dessa pessoa para de funcionar, e ela sai de todos os aparelhos.</li>
          <li>A senha não muda. Na próxima entrada, depois da senha, aparece um QR Code novo.</li>
          <li>Faça isso só depois de confirmar com ela, pessoalmente ou por telefone.</li>
        </ul>
        <Input
          label="Seu código de autenticação"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="000000"
          leftIcon={<KeyRound className="size-4" />}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          error={error}
        />
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={submitting}>Redefinir autenticador</Button>
        </div>
      </form>
    </Modal>
  );
}

function MemberStatusPill({ status }: { status: MembershipStatus }) {
  const labels: Record<MembershipStatus, string> = {
    ACTIVE: 'Ativo',
    INVITED: 'Convite pendente',
    SUSPENDED: 'Suspenso',
    DISABLED: 'Inativo',
  };
  const tones: Record<MembershipStatus, StatusTone> = {
    ACTIVE: 'positive',
    INVITED: 'brand',
    SUSPENDED: 'warning',
    DISABLED: 'neutral',
  };
  return <StatusPill tone={tones[status]}>{labels[status]}</StatusPill>;
}
